import * as TE from "fp-ts/TaskEither"
import { BehaviorSubject, Observable } from "rxjs"
import { cloneDeep } from "lodash-es"
import { HoppRESTResponse } from "./types/HoppRESTResponse"
import { EffectiveHoppRESTRequest } from "./utils/EffectiveURL"
import { getService } from "~/modules/dioc"
import { KernelInterceptorService } from "~/services/kernel-interceptor.service"
import { RESTRequest, RESTResponse } from "~/helpers/kernel/rest"
import { RelayError } from "@hoppscotch/kernel"
import type { HoppRESTRequest } from "@hoppscotch/data"

export type NetworkStrategy = (
  req: EffectiveHoppRESTRequest
) => TE.TaskEither<RelayError, HoppRESTResponse>

export function createRESTNetworkRequestStream(
  request: EffectiveHoppRESTRequest,
  options?: {
    responseRequest?: HoppRESTRequest
    sanitizeResponse?: (response: HoppRESTResponse) => HoppRESTResponse
    beforeExecute?: () => void
  }
): [Observable<HoppRESTResponse>, () => void] {
  const safeRequest = cloneDeep(options?.responseRequest ?? request)
  const response = new BehaviorSubject<HoppRESTResponse>({
    type: "loading",
    req: safeRequest,
  })
  const emit = (value: HoppRESTResponse) => {
    const safe = "req" in value ? { ...value, req: safeRequest } : value
    response.next(
      options?.sanitizeResponse ? options.sanitizeResponse(safe) : safe
    )
  }

  const req = cloneDeep(request)
  let cancelled = false

  const execResult = RESTRequest.toRequest(req).then((kernelRequest) => {
    if (!kernelRequest) {
      emit({
        type: "network_fail",
        req,
        error: new Error("Failed to create kernel request"),
      })
      response.complete()
      return
    }

    if (cancelled) return
    options?.beforeExecute?.()
    return service.execute(kernelRequest)
  })

  const service = getService(KernelInterceptorService)

  execResult
    .then((result) => {
      if (!result) return

      return result.response.then(async (res) => {
        if (res._tag === "Right") {
          const processedRes = await RESTResponse.toResponse(
            res.right,
            safeRequest
          )

          if (processedRes.type === "success") {
            emit(processedRes)
          } else {
            emit({
              type: "network_fail",
              req,
              error: processedRes.error,
            })
          }
        } else {
          emit({
            type: "interceptor_error",
            req,
            error: res.left,
          })
        }
        response.complete()
      })
    })
    .catch((error: unknown) => {
      emit({ type: "network_fail", req: safeRequest, error })
      response.complete()
    })

  return [
    response,
    async () => {
      cancelled = true
      try {
        const result = await execResult
        if (result) await result.cancel()
      } catch (_error) {
        // Ignore cancel errors - request may have already completed
        // This is expected behavior and not an actual error
      }
    },
  ]
}
